import axios from 'axios';
import useAuthStore from '../store/auth';
import Auth from './Auth';
import useSignupStore from '../store/signup';

const authUrls = [
  '/auth/login/',
  '/auth/token/refresh/',
  '/api/token/refresh/',
  '/auth/password/reset/',
];

const refreshAccessToken = async () => {
  try {
    const refreshToken =
      Auth.refreshToken || localStorage.getItem('signup_refreshToken');

    if (!refreshToken) {
      return null;
    }

    // Try both possible refresh endpoints
    let res;
    try {
      // Try /api/token/refresh/ first (matches /api/token/ pattern)
      res = await ax.post('/api/token/refresh/', {
        refresh: refreshToken,
      });
    } catch (e) {
      // If that fails, try /auth/token/refresh/
      if (e.response?.status === 404) {
        res = await ax.post('/auth/token/refresh/', {
          refresh: refreshToken,
        });
      } else {
        throw e;
      }
    }

    const access = res.data.access;

    // Save the new access token in both Auth and signupStore
    if (Auth.refreshToken) {
      Auth.accessToken = access;
    } else if (localStorage.getItem('signup_refreshToken')) {
      useSignupStore.getState().setAuthToken(access);
    }

    return access;
  } catch (e) {
    // If endpoint doesn't exist (404), don't treat it as a critical error
    if (e.response?.status === 404) {
      console.warn(
        'Token refresh endpoint not found (404). Backend may not support token refresh.'
      );
      // Don't clear auth state for 404 - just return null
      return null;
    }

    console.error('Failed to refresh access token. Error:', e);

    // Explicitly log which refresh token was used
    console.log(
      'Refresh token used:',
      Auth.refreshToken || localStorage.getItem('signup_refreshToken')
    );

    // Only clear user state when the refresh truly fails (not 404)
    if (e.response?.status !== 404) {
      useAuthStore.setState({
        user: null,
        authenticated: false,
        checkingAuth: false,
      });
    }
    return null;
  }
};

const ax = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

ax.interceptors.request.use((config) => {
  // CRITICAL: Sanitize URL FIRST - ensure no spaces (replace with underscores)
  // This must happen before any other processing to prevent 404 errors
  if (config.url) {
    const originalUrl = config.url;
    // Replace all spaces, tabs, and other whitespace with underscores
    // This fixes URLs like "check user signup status" → "check_user_signup_status"
    config.url = String(config.url).replace(/[\s\t\n\r]+/g, '_');

    // Also sanitize query string parameters in the URL
    if (config.url.includes('?')) {
      const [baseUrl, queryString] = config.url.split('?');
      if (queryString) {
        // Sanitize query string - replace spaces in parameter values
        const sanitizedQuery = queryString
          .split('&')
          .map((param) => {
            const [key, value] = param.split('=');
            const sanitizedValue = value
              ? decodeURIComponent(value).replace(/[\s\t\n\r]+/g, '_')
              : value;
            return `${key}=${encodeURIComponent(sanitizedValue)}`;
          })
          .join('&');
        config.url = `${baseUrl}?${sanitizedQuery}`;
      }
    }

    // Also sanitize the baseURL if it exists
    if (config.baseURL && config.baseURL.includes(' ')) {
      config.baseURL = config.baseURL.replace(/[\s\t\n\r]+/g, '_');
    }

    // Log if we had to sanitize (for debugging)
    if (originalUrl !== config.url) {
      console.warn(
        '⚠️ URL sanitized (spaces removed):',
        originalUrl,
        '→',
        config.url
      );
    }
  }

  // Skip auth token attachment for authentication endpoints
  if (authUrls.includes(config.url) || config.url?.includes('/api/token/')) {
    return config;
  }

  // Priority order for token retrieval:
  // 1. signupStore.authToken (from /api/token/ - this is the JWT token for API auth)
  // 2. signup_accessToken from localStorage (backup of signupStore token)
  // 3. Auth.accessToken (from /auth/login/ - might be a different type of token)
  // 4. ACCESS_TOKEN from localStorage (backup of Auth token)
  const { authToken } = useSignupStore.getState();
  const accessFromSignupStorage = localStorage.getItem('signup_accessToken');
  const accessFromAuth = Auth.accessToken;
  const accessFromDirectStorage = localStorage.getItem('ACCESS_TOKEN');

  // Prefer token from /api/token/ (JWT) over token from /auth/login/ (key field)
  const accessToken =
    authToken ||
    accessFromSignupStorage ||
    accessFromAuth ||
    accessFromDirectStorage;

  if (accessToken) {
    // Determine authentication format
    // Django REST Framework TokenAuthentication (40-char hex tokens) uses "Token" prefix
    // JWT tokens (longer, base64-like) use "Bearer" prefix
    // Auto-detect: 40-char hex strings = Django Token, use "Token"
    // Can be overridden with REACT_APP_AUTH_PREFIX env var
    let authPrefix;
    if (process.env.REACT_APP_AUTH_PREFIX !== undefined) {
      authPrefix = process.env.REACT_APP_AUTH_PREFIX;
    } else {
      // Auto-detect: 40-char tokens are typically Django TokenAuthentication
      const isDjangoToken =
        accessToken.length === 40 && /^[a-f0-9]{40}$/i.test(accessToken);
      authPrefix = isDjangoToken ? 'Token' : 'Bearer';
    }

    let tokenValue;
    if (accessToken.startsWith('Bearer ') || accessToken.startsWith('Token ')) {
      // Token already has prefix
      tokenValue = accessToken;
    } else if (authPrefix === '') {
      // No prefix - send token as-is
      tokenValue = accessToken;
    } else {
      // Apply the configured/auto-detected prefix
      tokenValue = `${authPrefix} ${accessToken}`;
    }
    config.headers.Authorization = tokenValue;

    // Only log if using wrong token source (for debugging)
    if (!authToken && !accessFromSignupStorage && accessFromAuth) {
      console.warn(
        `[REQUEST] Using token from /auth/login/ instead of /api/token/. Token may be invalid.`
      );
    }
  } else {
    console.warn('[REQUEST] ⚠️ No access token found for request:', config.url);
  }

  // Only set Content-Type to application/json if the request data is not FormData
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

ax.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config: originalRequest, response } = error;

    // Special handling for 401 errors
    if (response?.status === 401) {
      const authHeader = originalRequest?.headers?.Authorization;
      const hasTokenFromApiToken =
        !!useSignupStore.getState().authToken ||
        !!localStorage.getItem('signup_accessToken');
      console.error('🔴 401 Unauthorized:', {
        url: originalRequest?.url,
        hasAuthHeader: !!authHeader,
        usingTokenFromApiToken: hasTokenFromApiToken,
        authHeaderPreview: authHeader
          ? `${authHeader.substring(0, 40)}...`
          : 'NONE',
        backendResponse: response?.data,
      });

      if (!hasTokenFromApiToken) {
        console.warn(
          '⚠️ Using token from /auth/login/ instead of /api/token/. Token may be invalid. Please log out and log back in.'
        );
      }
    } else if (response?.status === 500) {
      // Special handling for 500 errors - backend server error
      console.error('🔴 500 Internal Server Error:', {
        url: originalRequest?.url,
        message: 'Backend server error. Check backend logs for details.',
        responseType:
          typeof response.data === 'string' &&
          response.data.includes('<!DOCTYPE')
            ? 'HTML Error Page'
            : 'JSON',
      });
    } else if (response?.status === 404) {
      // 404 errors are usually expected for optional endpoints
      // Only log if it's not an optional endpoint
      const optionalEndpoints = [
        '/api/users/dashboard-view/',
        '/api/users/check_user_signup_status/',
      ];
      if (
        !optionalEndpoints.some((endpoint) =>
          originalRequest?.url?.includes(endpoint)
        )
      ) {
        console.warn('⚠️ 404 Not Found:', originalRequest?.url);
      }
    } else {
      // Only log other errors if they're not network errors
      if (response) {
        console.error('Axios Error:', {
          url: originalRequest?.url,
          status: response.status,
          message: error.message,
        });
      }
    }

    // Handle CORS or network errors
    if (!response) {
      console.error(
        'Network Error - Check if backend is running and CORS is configured:',
        {
          url: originalRequest?.url,
          baseURL: originalRequest?.baseURL,
          fullURL: `${originalRequest?.baseURL}${originalRequest?.url}`,
        }
      );
      // Don't logout on network errors during login/signup
      if (authUrls.some((url) => originalRequest?.url?.includes(url))) {
        return Promise.reject(error);
      }
    }

    if (response?.status === 503) {
      window.location.href = '/503';
    }

    // Only try to refresh token if we have a 401 and it's not a login/auth endpoint
    if (
      originalRequest.url !== '/auth/token/refresh/' &&
      originalRequest.url !== '/api/token/refresh/' &&
      !originalRequest._retry &&
      response?.status === 401 &&
      !authUrls.some((url) => originalRequest.url?.includes(url))
    ) {
      originalRequest._retry = true;
      const access = await refreshAccessToken();

      if (access) {
        // Use the same auth prefix as configured (default: "Token" for Django)
        const authPrefix =
          process.env.REACT_APP_AUTH_PREFIX !== undefined
            ? process.env.REACT_APP_AUTH_PREFIX
            : 'Token';
        const tokenValue =
          authPrefix === '' ? access : `${authPrefix} ${access}`;
        ax.defaults.headers.common['Authorization'] = tokenValue;
        originalRequest.headers.Authorization = tokenValue;
        return ax(originalRequest);
      } else {
        // Token refresh failed or endpoint doesn't exist
        // If refresh endpoint returns 404, don't logout - just let the 401 pass through
        // The user will need to login again, but don't force logout on 404
        if (response?.status === 401) {
          // Only logout if we're not on a login page and refresh actually failed (not 404)
          if (!window.location.pathname.includes('/login')) {
            // Check if refresh endpoint exists by looking at the error
            // If it's a 404, the endpoint doesn't exist, so don't force logout
            console.warn(
              'Token refresh unavailable or failed. User may need to login again.'
            );
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export default ax;
